/**
 * Lazy-loads the reCAPTCHA v3 client and produces tokens for form submissions.
 *
 * Backend mirrors the same fail-open contract: if no `siteKey` is configured
 * we return null and the request goes through bare; if backend has reCAPTCHA
 * disabled in site_settings it accepts requests without a token.
 *
 * Usage:
 *   const token = await getRecaptchaToken('login', siteKey);
 *   if (token) headers['x-recaptcha-token'] = token;
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, opts: { action: string }): Promise<string>;
    };
  }
}

let scriptLoaded = false;
let loadPromise: Promise<void> | null = null;

function loadScript(siteKey: string): Promise<void> {
  if (scriptLoaded) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => { scriptLoaded = true; resolve(); };
    s.onerror = () => { loadPromise = null; reject(new Error('reCAPTCHA failed to load')); };
    document.head.appendChild(s);
  });
  return loadPromise;
}

export async function getRecaptchaToken(action: string, siteKey: string): Promise<string | null> {
  if (!siteKey) return null;
  if (typeof window === 'undefined') return null;
  try {
    await loadScript(siteKey);
    return await new Promise<string>((resolve, reject) => {
      // grecaptcha.ready waits for the bundled wrapper to finish init.
      window.grecaptcha?.ready(async () => {
        try {
          const token = await window.grecaptcha!.execute(siteKey, { action });
          resolve(token);
        } catch (e) { reject(e); }
      });
    });
  } catch {
    // Script blocked / network failed — fail open. Backend has the same
    // policy when its own siteverify call fails, so the two stay in sync.
    return null;
  }
}
